import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, query, orderBy, where, Timestamp, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend } from 'recharts';
import * as XLSX from 'xlsx';
import printTable from '../utils/printUtils';
import {
  buildSalesLossMap,
  computeVelocityData,
  computeSlowMovers,
  computeOverstockedProducts,
  computeProductSales,
  enrichWithMargins,
  computeBestSellers,
  computeLeastSellers,
  computeDeadStock,
  computeRevenueByGroup,
  computeTopRevenue,
  computeTotals,
  computeCapitalTiedUp,
} from '../utils/analyticsUtils';

const SALE_TYPES = ['sale'];
const LOSS_TYPES = ['writeoff'];

const mobileStyles = {
  card: { backgroundColor: '#ffffff', borderTop: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', borderLeft: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px', marginBottom: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  cardTopRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' },
  rank: { fontSize: '11px', fontWeight: '700', color: '#94a3b8', marginRight: '6px' },
  name: { fontSize: '14px', fontWeight: '600', color: '#1e293b' },
  badge: { padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '700', flexShrink: 0 },
  metaRow: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  groupBadge: { backgroundColor: '#eff6ff', color: '#3b82f6', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '500' },
  metaText: { fontSize: '12px', color: '#64748b' },
};

const styles = {
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
  title: { fontSize: '24px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' },
  subtitle: { fontSize: '14px', color: '#64748b' },
  periodSelector: { display: 'flex', alignItems: 'center', gap: '8px' },
  periodLabel: { fontSize: '13px', color: '#64748b' },
  select: { padding: '8px 14px', backgroundColor: '#ffffff', borderTop: '1.5px solid #e2e8f0', borderRight: '1.5px solid #e2e8f0', borderBottom: '1.5px solid #e2e8f0', borderLeft: '1.5px solid #e2e8f0', borderRadius: '8px', color: '#1e293b', fontSize: '13px', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  cards: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '24px' },
  card: { borderRadius: '12px', padding: '20px', borderTop: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', borderLeft: '1px solid #e2e8f0', transition: 'all 0.2s ease', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  cardValue: { fontSize: '32px', fontWeight: '800', marginBottom: '4px' },
  cardLabel: { fontSize: '13px', color: '#64748b', fontWeight: '500' },
  pageTabs: { display: 'flex', gap: '0', marginBottom: '28px', borderBottom: '2px solid #e2e8f0' },
  pageTabActive: { padding: '10px 20px', borderTop: 'none', borderRight: 'none', borderLeft: 'none', background: 'none', borderBottom: '2px solid #3b82f6', marginBottom: '-2px', color: '#3b82f6', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  pageTabInactive: { padding: '10px 20px', borderTop: 'none', borderRight: 'none', borderBottom: 'none', borderLeft: 'none', background: 'none', color: '#94a3b8', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  tabs: { display: 'flex', gap: '4px', marginBottom: '20px', backgroundColor: '#f1f5f9', padding: '4px', borderRadius: '10px', width: 'fit-content' },
  tabActive: { padding: '8px 18px', borderRadius: '8px', borderTop: 'none', borderRight: 'none', borderBottom: 'none', borderLeft: 'none', backgroundColor: '#ffffff', color: '#1e293b', fontSize: '13px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.1)', whiteSpace: 'nowrap' },
  tabInactive: { padding: '8px 18px', borderRadius: '8px', borderTop: 'none', borderRight: 'none', borderBottom: 'none', borderLeft: 'none', backgroundColor: 'transparent', color: '#64748b', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' },
  sectionSubtitle: { fontSize: '13px', color: '#64748b' },
  exportBtn: { padding: '8px 16px', backgroundColor: '#ffffff', borderTop: '1.5px solid #e2e8f0', borderRight: '1.5px solid #e2e8f0', borderBottom: '1.5px solid #e2e8f0', borderLeft: '1.5px solid #e2e8f0', color: '#475569', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  chartWrapper: { backgroundColor: '#ffffff', borderRadius: '12px', padding: '20px', marginBottom: '16px', borderTop: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', borderLeft: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  thresholdRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  thresholdInput: { width: '70px', padding: '7px 10px', backgroundColor: '#ffffff', borderTop: '1.5px solid #e2e8f0', borderRight: '1.5px solid #e2e8f0', borderBottom: '1.5px solid #e2e8f0', borderLeft: '1.5px solid #e2e8f0', borderRadius: '8px', color: '#1e293b', fontSize: '13px' },
  emptyState: { textAlign: 'center', padding: '60px 24px', backgroundColor: '#ffffff', borderRadius: '12px', borderTop: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', borderLeft: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  emptyTitle: { fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '8px' },
  emptySubtitle: { fontSize: '13px', color: '#94a3b8' },
  tableWrapper: { backgroundColor: '#ffffff', borderRadius: '12px', borderTop: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', borderLeft: '1px solid #e2e8f0', overflow: 'auto', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '500px' },
  th: { padding: '13px 16px', textAlign: 'left', color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', backgroundColor: '#f8fafc', whiteSpace: 'nowrap', borderBottom: '1px solid #e2e8f0' },
  tr: { borderBottom: '1px solid #f1f5f9', transition: 'all 0.15s ease' },
  tdName: { padding: '13px 16px', color: '#1e293b', fontSize: '14px', fontWeight: '500' },
  td: { padding: '13px 16px', color: '#374151', fontSize: '14px' },
  groupBadge: { backgroundColor: '#eff6ff', color: '#3b82f6', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '500' },
  soldValue: { backgroundColor: '#eff6ff', color: '#3b82f6', padding: '3px 12px', borderRadius: '6px', fontWeight: '700', fontSize: '14px' },
  slowValue: { backgroundColor: '#fff7ed', color: '#f97316', padding: '3px 12px', borderRadius: '6px', fontWeight: '700', fontSize: '14px' },
  lossValue: { backgroundColor: '#fff7ed', color: '#f97316', padding: '3px 12px', borderRadius: '6px', fontWeight: '700', fontSize: '14px' },
  overstockValue: { backgroundColor: '#fef2f2', color: '#ef4444', padding: '3px 12px', borderRadius: '6px', fontWeight: '700', fontSize: '14px' },
  revenueValue: { backgroundColor: '#f0fdf4', color: '#16a34a', padding: '3px 12px', borderRadius: '6px', fontWeight: '700', fontSize: '14px' },
  deadValue: { backgroundColor: '#fdf4ff', color: '#9333ea', padding: '3px 12px', borderRadius: '6px', fontWeight: '700', fontSize: '14px' },
};

const PIE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#84cc16'];

function PeriodSelector({ days, setDays }) {
  return (
    <div style={styles.periodSelector}>
      <label style={styles.periodLabel}>Period:</label>
      <select value={days} onChange={e => setDays(Number(e.target.value))} style={styles.select}>
        {[
          { value: 7,   label: 'Last 7 days' },
          { value: 14,  label: 'Last 14 days' },
          { value: 30,  label: 'Last 30 days' },
          { value: 60,  label: 'Last 60 days' },
          { value: 90,  label: 'Last 90 days' },
          { value: 180, label: 'Last 6 months' },
          { value: 365, label: 'Last 1 year' },
          { value: 0,   label: 'All time' },
        ].map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
}

function InventoryPage({ products, logs, days, setDays, isMobile }) {
  const [slowThreshold, setSlowThreshold] = useState(2);
  const [overstockDays, setOverstockDays] = useState(60);
  const [activeTab, setActiveTab] = useState('velocity');

  const saleLogs = logs.filter(l => SALE_TYPES.includes(l.transactionType));
  const lossLogs = logs.filter(l => LOSS_TYPES.includes(l.transactionType));
  const hasSpecificPeriod = days !== 0;
  const periodLabel = days === 0 ? 'All time' : `${days}d`;

  const productMap = buildSalesLossMap(saleLogs, lossLogs);
  const velocityData = computeVelocityData(productMap, days);
  const slowMovers = computeSlowMovers(velocityData, slowThreshold, hasSpecificPeriod);
  const overstockedProducts = computeOverstockedProducts(products, velocityData, overstockDays, hasSpecificPeriod);

  const totalSold = saleLogs.reduce((s, l) => s + l.quantity, 0);
  const totalLost = lossLogs.reduce((s, l) => s + l.quantity, 0);

  const exportVelocity = () => {
    const rows = velocityData.map(p => ({
      'Product': p.name,
      'Group': p.group,
      [`Total Sold (${periodLabel})`]: p.totalSold,
      'Avg Units/Day': p.avgPerDay ?? '—',
      [`Write-offs (${periodLabel})`]: p.totalLost,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sales Velocity');
    XLSX.writeFile(wb, 'sales-velocity.xlsx');
  };
  const printVelocity = () => {
  printTable({
    title: 'Sales Velocity Report',
    subtitle: `${periodLabel} · ${velocityData.length} products with sales`,
    columns: ['Product', 'Group', `Total Sold (${periodLabel})`, 'Avg Units/Day', `Write-offs (${periodLabel})`],
    rows: velocityData.map(p => [
      p.name,
      p.group || '—',
      { value: p.totalSold, style: 'color:#3b82f6;font-weight:700;' },
      p.avgPerDay ?? '—',
      p.totalLost > 0 ? { value: p.totalLost, style: 'color:#f97316;font-weight:700;' } : '—',
    ]),
    styles: { colWidths: ['28%', '16%', '18%', '18%', '20%'] },
  });
};

  const summaryCards = [
    { label: 'Products',    value: products.length,        color: '#3b82f6', bg: '#eff6ff' },
    { label: 'Units Sold',  value: totalSold,              color: '#16a34a', bg: '#f0fdf4' },
    { label: 'Write-offs',  value: totalLost,              color: '#f97316', bg: '#fff7ed' },
    { label: 'Slow Movers', value: slowMovers.length,      color: '#ef4444', bg: '#fef2f2' },
  ];

  const tabs = [
    { key: 'velocity',  label: '📈 Sales Velocity' },
    { key: 'slow',      label: `🐢 Slow Movers (${slowMovers.length})` },
    { key: 'overstock', label: `📦 Overstocked (${overstockedProducts.length})` },
  ];

  const MobileVelocityCards = () => (
    <div>
      {velocityData.map((p, i) => (
        <div key={i} style={mobileStyles.card}>
          <div style={mobileStyles.cardTopRow}>
            <div>
              <span style={mobileStyles.rank}>#{i + 1}</span>
              <span style={mobileStyles.name}>{p.name}</span>
            </div>
            <span style={{ ...mobileStyles.badge, backgroundColor: '#eff6ff', color: '#3b82f6' }}>{p.totalSold} sold</span>
          </div>
          <div style={mobileStyles.metaRow}>
            <span style={mobileStyles.groupBadge}>{p.group}</span>
            <span style={mobileStyles.metaText}>{p.avgPerDay !== null ? `${p.avgPerDay}/day` : '—'}</span>
            {p.totalLost > 0 && (
              <span style={{ ...mobileStyles.badge, backgroundColor: '#fff7ed', color: '#f97316' }}>{p.totalLost} written off</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  const MobileSlowCards = () => (
    <div>
      {slowMovers.map((p, i) => (
        <div key={i} style={mobileStyles.card}>
          <div style={mobileStyles.cardTopRow}>
            <span style={mobileStyles.name}>{p.name}</span>
            <span style={{ ...mobileStyles.badge, backgroundColor: '#fff7ed', color: '#f97316' }}>{p.avgPerDay}/day</span>
          </div>
          <div style={mobileStyles.metaRow}>
            <span style={mobileStyles.groupBadge}>{p.group}</span>
            <span style={mobileStyles.metaText}>{p.totalSold} sold total</span>
          </div>
        </div>
      ))}
    </div>
  );

  const MobileOverstockCards = () => (
    <div>
      {overstockedProducts.map((p, i) => (
        <div key={i} style={mobileStyles.card}>
          <div style={mobileStyles.cardTopRow}>
            <span style={mobileStyles.name}>{p.name}</span>
            <span style={{ ...mobileStyles.badge, backgroundColor: '#fef2f2', color: '#ef4444' }}>
              {p.daysOfSupply === Infinity ? '∞ days' : `${p.daysOfSupply} days`}
            </span>
          </div>
          <div style={mobileStyles.metaRow}>
            <span style={mobileStyles.groupBadge}>{p.group}</span>
            <span style={mobileStyles.metaText}>Stock: {p.stock}</span>
            <span style={mobileStyles.metaText}>
              {p.velocity > 0 ? `${p.velocity}/day` : <span style={{ color: '#ef4444' }}>no sales</span>}
            </span>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div>
      <div style={styles.header}>
        <div>
          <p style={styles.subtitle}>
            {days === 0 ? 'All time stock insights' : `Stock insights for the last ${days} days`}
          </p>
        </div>
        <PeriodSelector days={days} setDays={setDays} />
      </div>

      <div style={styles.cards}>
        {summaryCards.map(card => (
          <div key={card.label} style={{ ...styles.card, backgroundColor: card.bg }}>
            <p style={{ ...styles.cardValue, color: card.color }}>{card.value}</p>
            <p style={styles.cardLabel}>{card.label}</p>
          </div>
        ))}
      </div>

      <div style={{ ...styles.tabs, ...(isMobile ? { width: '100%', overflowX: 'auto' } : {}) }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            style={activeTab === t.key ? styles.tabActive : styles.tabInactive}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'velocity' && (
        <div>
          <div style={styles.sectionHeader}>
            <p style={styles.sectionSubtitle}>
              {days === 0
                ? 'All time units sold per product (daily rate unavailable for all-time)'
                : `Units sold per product over the last ${days} days`}
            </p>
   <div style={{ display: 'flex', gap: '8px' }}>
  <button onClick={exportVelocity} style={styles.exportBtn}>⬇ Export Excel</button>
  <button onClick={printVelocity} style={styles.exportBtn}>🖨️ Print</button>
</div>
          </div>
          {velocityData.length === 0 ? (
            <div style={styles.emptyState}>
              <p style={styles.emptyTitle}>No sales recorded yet</p>
              <p style={styles.emptySubtitle}>Record a Sale transaction to see velocity data</p>
            </div>
          ) : (
            <>
              <div style={styles.chartWrapper}>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={velocityData.slice(0, 15)} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
                    <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} angle={-35} textAnchor="end" interval={0} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#ffffff', borderTop: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', borderLeft: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                      labelStyle={{ color: '#1e293b', fontWeight: '600' }}
                    />
                    <Bar dataKey="totalSold" radius={[6, 6, 0, 0]} name="Units Sold">
                      {velocityData.slice(0, 15).map((_, i) => (
                        <Cell key={i} fill={i === 0 ? '#3b82f6' : i === 1 ? '#60a5fa' : i === 2 ? '#93c5fd' : '#dbeafe'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {isMobile ? <MobileVelocityCards /> : (
                <div style={styles.tableWrapper}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        {['#', 'Product', 'Group', `Total Sold (${periodLabel})`, 'Avg/Day', `Write-offs (${periodLabel})`].map(h => (
                          <th key={h} style={styles.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {velocityData.map((p, i) => (
                        <tr key={i} style={styles.tr}>
                          <td style={{ ...styles.td, color: '#94a3b8', fontWeight: '600' }}>{i + 1}</td>
                          <td style={styles.tdName}>{p.name}</td>
                          <td style={styles.td}><span style={styles.groupBadge}>{p.group}</span></td>
                          <td style={styles.td}><span style={styles.soldValue}>{p.totalSold}</span></td>
                          <td style={{ ...styles.td, color: '#64748b' }}>{p.avgPerDay !== null ? `${p.avgPerDay}/day` : '—'}</td>
                          <td style={styles.td}>
                            {p.totalLost > 0
                              ? <span style={styles.lossValue}>{p.totalLost}</span>
                              : <span style={{ color: '#cbd5e1' }}>—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === 'slow' && (
        <div>
          <div style={styles.sectionHeader}>
            <p style={styles.sectionSubtitle}>
              {!hasSpecificPeriod
                ? 'Select a specific period to see slow movers'
                : `Products with sales below ${slowThreshold} units/day`}
            </p>
            {hasSpecificPeriod && (
              <div style={styles.thresholdRow}>
                <label style={styles.periodLabel}>Threshold:</label>
                <input type="number" min="0.1" step="0.5" value={slowThreshold}
                  onChange={e => setSlowThreshold(Number(e.target.value))} style={styles.thresholdInput} />
                <span style={styles.periodLabel}>units/day</span>
              </div>
            )}
          </div>
          {!hasSpecificPeriod ? (
            <div style={styles.emptyState}>
              <p style={styles.emptyTitle}>Not available for All time</p>
              <p style={styles.emptySubtitle}>Select a specific period to calculate slow movers</p>
            </div>
          ) : slowMovers.length === 0 ? (
            <div style={styles.emptyState}>
              <p style={styles.emptyTitle}>No slow movers at this threshold</p>
              <p style={styles.emptySubtitle}>All products are selling above {slowThreshold} units/day</p>
            </div>
          ) : isMobile ? <MobileSlowCards /> : (
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {['Product', 'Group', `Total Sold (${periodLabel})`, 'Avg/Day'].map(h => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {slowMovers.map((p, i) => (
                    <tr key={i} style={styles.tr}>
                      <td style={styles.tdName}>{p.name}</td>
                      <td style={styles.td}><span style={styles.groupBadge}>{p.group}</span></td>
                      <td style={styles.td}><span style={styles.slowValue}>{p.totalSold}</span></td>
                      <td style={{ ...styles.td, color: '#f97316', fontWeight: '600' }}>{p.avgPerDay}/day</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'overstock' && (
        <div>
          <div style={styles.sectionHeader}>
            <p style={styles.sectionSubtitle}>
              {!hasSpecificPeriod
                ? 'Select a specific period to calculate days of supply'
                : `Products with more than ${overstockDays} days of supply at current sales rate`}
            </p>
            {hasSpecificPeriod && (
              <div style={styles.thresholdRow}>
                <label style={styles.periodLabel}>Days of supply:</label>
                <input type="number" min="1" value={overstockDays}
                  onChange={e => setOverstockDays(Number(e.target.value))} style={styles.thresholdInput} />
              </div>
            )}
          </div>
          {!hasSpecificPeriod ? (
            <div style={styles.emptyState}>
              <p style={styles.emptyTitle}>Not available for All time</p>
              <p style={styles.emptySubtitle}>Select a specific period to calculate overstock</p>
            </div>
          ) : overstockedProducts.length === 0 ? (
            <div style={styles.emptyState}>
              <p style={styles.emptyTitle}>No overstocked products</p>
              <p style={styles.emptySubtitle}>All products are within {overstockDays} days of supply</p>
            </div>
          ) : isMobile ? <MobileOverstockCards /> : (
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {['Product', 'Group', 'Current Stock', 'Avg Sales/Day', 'Days of Supply'].map(h => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {overstockedProducts.map((p, i) => (
                    <tr key={i} style={styles.tr}>
                      <td style={styles.tdName}>{p.name}</td>
                      <td style={styles.td}><span style={styles.groupBadge}>{p.group}</span></td>
                      <td style={{ ...styles.td, fontWeight: '600', color: '#1e293b' }}>{p.stock}</td>
                      <td style={{ ...styles.td, color: '#64748b' }}>
                        {p.velocity > 0 ? `${p.velocity}/day` : <span style={{ color: '#ef4444' }}>no sales</span>}
                      </td>
                      <td style={styles.td}>
                        <span style={styles.overstockValue}>
                          {p.daysOfSupply === Infinity ? '∞ days' : `${p.daysOfSupply} days`}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ValueSalesPage({ products, logs, days, setDays, isMobile }) {
  const [activeTab, setActiveTab] = useState('bestsellers');

  const saleLogs = logs.filter(l => SALE_TYPES.includes(l.transactionType));
  const hasSpecificPeriod = days !== 0;
  const periodLabel = days === 0 ? 'All time' : `${days}d`;

  const productSalesMap = computeProductSales(saleLogs, products);
  const enriched = enrichWithMargins(productSalesMap, products);

  const bestSellers = computeBestSellers(enriched);
  const leastSellers = computeLeastSellers(enriched);

  const deadStock = computeDeadStock(products, productSalesMap);

  const groupRevPie = computeRevenueByGroup(enriched);

  const topRevenue = computeTopRevenue(enriched);

  const { totalRevenue, totalUnitsSold } = computeTotals(enriched);

  const summaryCards = [
    { label: 'Total Revenue',  value: totalRevenue > 0 ? `₹${Math.round(totalRevenue).toLocaleString('en-IN')}` : '—', color: '#16a34a', bg: '#f0fdf4' },
    { label: 'Units Sold',     value: totalUnitsSold,   color: '#3b82f6', bg: '#eff6ff' },
    { label: 'Dead Stock SKUs', value: deadStock.length, color: '#9333ea', bg: '#fdf4ff' },
    { label: 'Products Sold',  value: enriched.length,  color: '#f97316', bg: '#fff7ed' },
  ];

  const tabs = [
    { key: 'bestsellers', label: '🏆 Best Sellers' },
    { key: 'revenue',     label: `💰 Revenue` },
    { key: 'leastsold',   label: `📉 Least Sold` },
    { key: 'deadstock',   label: `💀 Dead Stock (${deadStock.length})` },
  ];

  const exportData = () => {
    const rows = bestSellers.map(p => ({
      'Product': p.name,
      'Group': p.group,
      [`Units Sold (${periodLabel})`]: p.totalUnits,
      [`Revenue (${periodLabel})`]: p.totalRevenue > 0 ? `₹${Math.round(p.totalRevenue)}` : '—',
      'Selling Price': p.sellingPrice > 0 ? `₹${p.sellingPrice}` : '—',
      'Cost Price':    p.costPrice    > 0 ? `₹${p.costPrice}`    : '—',
      'Avg Margin %':  p.avgMargin ?? '—',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Value & Sales');
    XLSX.writeFile(wb, 'value-sales.xlsx');
  };
  const printValueSales = () => {
  printTable({
    title: 'Value & Sales Report',
    subtitle: `${periodLabel} · Total revenue: ₹${Math.round(totalRevenue).toLocaleString('en-IN')} · ${totalUnitsSold} units sold`,
    columns: ['Product', 'Group', `Units Sold (${periodLabel})`, `Revenue (${periodLabel})`, 'Selling Price', 'Cost Price', 'Margin %'],
    rows: bestSellers.map(p => [
      p.name,
      p.group || '—',
      { value: p.totalUnits, style: 'color:#3b82f6;font-weight:700;' },
      p.totalRevenue > 0 ? { value: `₹${Math.round(p.totalRevenue).toLocaleString('en-IN')}`, style: 'color:#16a34a;font-weight:700;' } : '—',
      p.sellingPrice > 0 ? `₹${p.sellingPrice}` : '—',
      p.costPrice > 0 ? `₹${p.costPrice}` : '—',
      p.avgMargin ?? '—',
    ]),
    styles: { colWidths: ['22%', '13%', '14%', '17%', '13%', '13%', '8%'] },
  });
};

  const MobileBestSellerCards = () => (
    <div>
      {bestSellers.map((p, i) => (
        <div key={i} style={mobileStyles.card}>
          <div style={mobileStyles.cardTopRow}>
            <div>
              <span style={mobileStyles.rank}>#{i + 1}</span>
              <span style={mobileStyles.name}>{p.name}</span>
            </div>
            <span style={{ ...mobileStyles.badge, backgroundColor: '#eff6ff', color: '#3b82f6' }}>{p.totalUnits} units</span>
          </div>
          <div style={mobileStyles.metaRow}>
            <span style={mobileStyles.groupBadge}>{p.group}</span>
            {p.totalRevenue > 0 && <span style={{ ...mobileStyles.badge, backgroundColor: '#f0fdf4', color: '#16a34a' }}>₹{Math.round(p.totalRevenue).toLocaleString('en-IN')}</span>}
            {p.avgMargin && <span style={mobileStyles.metaText}>{p.avgMargin}% margin</span>}
          </div>
        </div>
      ))}
    </div>
  );

  const MobileRevenueCards = () => (
    <div>
      {topRevenue.filter(p => p.totalRevenue > 0).map((p, i) => (
        <div key={i} style={mobileStyles.card}>
          <div style={mobileStyles.cardTopRow}>
            <div>
              <span style={mobileStyles.rank}>#{i + 1}</span>
              <span style={mobileStyles.name}>{p.name}</span>
            </div>
            <span style={{ ...mobileStyles.badge, backgroundColor: '#f0fdf4', color: '#16a34a' }}>
              ₹{Math.round(p.totalRevenue).toLocaleString('en-IN')}
            </span>
          </div>
          <div style={mobileStyles.metaRow}>
            <span style={mobileStyles.groupBadge}>{p.group}</span>
            <span style={mobileStyles.metaText}>{p.totalUnits} units</span>
            {p.avgMargin && <span style={mobileStyles.metaText}>{p.avgMargin}% margin</span>}
          </div>
        </div>
      ))}
    </div>
  );

  const MobileLeastSoldCards = () => (
    <div>
      {leastSellers.map((p, i) => (
        <div key={i} style={mobileStyles.card}>
          <div style={mobileStyles.cardTopRow}>
            <span style={mobileStyles.name}>{p.name}</span>
            <span style={{ ...mobileStyles.badge, backgroundColor: '#fff7ed', color: '#f97316' }}>{p.totalUnits} units</span>
          </div>
          <div style={mobileStyles.metaRow}>
            <span style={mobileStyles.groupBadge}>{p.group}</span>
            {p.totalRevenue > 0 && <span style={mobileStyles.metaText}>₹{Math.round(p.totalRevenue).toLocaleString('en-IN')} revenue</span>}
          </div>
        </div>
      ))}
    </div>
  );

  const MobileDeadStockCards = () => (
    <div>
      {deadStock.map((p, i) => {
        const tied = computeCapitalTiedUp(p);
        return (
          <div key={i} style={mobileStyles.card}>
            <div style={mobileStyles.cardTopRow}>
              <span style={mobileStyles.name}>{p.name}</span>
              <span style={{ ...mobileStyles.badge, backgroundColor: '#fdf4ff', color: '#9333ea' }}>{p.stock} in stock</span>
            </div>
            <div style={mobileStyles.metaRow}>
              <span style={mobileStyles.groupBadge}>{p.group}</span>
              {tied != null && (
                <span style={mobileStyles.metaText}>
                  Tied-up: ₹{tied.toLocaleString('en-IN')}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div>
      <div style={styles.header}>
        <div>
          <p style={styles.subtitle}>
            {days === 0 ? 'All time sales & revenue' : `Sales & revenue for the last ${days} days`}
          </p>
        </div>
        <PeriodSelector days={days} setDays={setDays} />
      </div>

      <div style={styles.cards}>
        {summaryCards.map(card => (
          <div key={card.label} style={{ ...styles.card, backgroundColor: card.bg }}>
            <p style={{ ...styles.cardValue, color: card.color, fontSize: card.label === 'Total Revenue' ? '22px' : '32px' }}>
              {card.value}
            </p>
            <p style={styles.cardLabel}>{card.label}</p>
          </div>
        ))}
      </div>

      {groupRevPie.length > 0 && (
        <div style={{ ...styles.chartWrapper, marginBottom: '20px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', marginBottom: '2px' }}>Revenue by Group</h3>
          <p style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '12px' }}>Share of total revenue per product group</p>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={groupRevPie}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={isMobile ? 70 : 85}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                labelLine={false}
              >
                {groupRevPie.map((_, i) => (
                  <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(v) => [`₹${v.toLocaleString('en-IN')}`, 'Revenue']}
                contentStyle={{ borderRadius: '8px', borderTop: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', borderLeft: '1px solid #e2e8f0', fontSize: '12px' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}

      <div style={{ ...styles.tabs, ...(isMobile ? { width: '100%', overflowX: 'auto' } : {}) }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            style={activeTab === t.key ? styles.tabActive : styles.tabInactive}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'bestsellers' && (
        <div>
          <div style={styles.sectionHeader}>
            <p style={styles.sectionSubtitle}>
              Products ranked by units sold — {periodLabel}
            </p>
           <div style={{ display: 'flex', gap: '8px' }}>
  <button onClick={exportData} style={styles.exportBtn}>⬇ Export Excel</button>
  <button onClick={printValueSales} style={styles.exportBtn}>🖨️ Print</button>
</div>
          </div>
          {bestSellers.length === 0 ? (
            <div style={styles.emptyState}>
              <p style={styles.emptyTitle}>No sales recorded yet</p>
              <p style={styles.emptySubtitle}>Record a Sale transaction to see best sellers</p>
            </div>
          ) : (
            <>
              <div style={styles.chartWrapper}>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={bestSellers.slice(0, 12)} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
                    <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} angle={-35} textAnchor="end" interval={0} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#ffffff', borderTop: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', borderLeft: '1px solid #e2e8f0', borderRadius: '8px' }}
                      formatter={(v) => [`${v} units`, 'Sold']}
                    />
                    <Bar dataKey="totalUnits" radius={[6, 6, 0, 0]} name="Units Sold">
                      {bestSellers.slice(0, 12).map((_, i) => (
                        <Cell key={i} fill={i === 0 ? '#3b82f6' : i === 1 ? '#60a5fa' : i === 2 ? '#93c5fd' : '#dbeafe'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {isMobile ? <MobileBestSellerCards /> : (
                <div style={styles.tableWrapper}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        {['#', 'Product', 'Group', `Units Sold (${periodLabel})`, `Revenue (${periodLabel})`, 'Margin %'].map(h => (
                          <th key={h} style={styles.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {bestSellers.map((p, i) => (
                        <tr key={i} style={styles.tr}>
                          <td style={{ ...styles.td, color: '#94a3b8', fontWeight: '600' }}>{i + 1}</td>
                          <td style={styles.tdName}>{p.name}</td>
                          <td style={styles.td}><span style={styles.groupBadge}>{p.group}</span></td>
                          <td style={styles.td}><span style={styles.soldValue}>{p.totalUnits}</span></td>
                          <td style={styles.td}>
                            {p.totalRevenue > 0
                              ? <span style={styles.revenueValue}>₹{Math.round(p.totalRevenue).toLocaleString('en-IN')}</span>
                              : <span style={{ color: '#cbd5e1' }}>—</span>}
                          </td>
                          <td style={{ ...styles.td, color: '#64748b' }}>{p.avgMargin ? `${p.avgMargin}%` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === 'revenue' && (
        <div>
          <div style={styles.sectionHeader}>
            <p style={styles.sectionSubtitle}>
              Products ranked by revenue generated — {periodLabel}
            </p>
<div style={{ display: 'flex', gap: '8px' }}>
  <button onClick={exportData} style={styles.exportBtn}>⬇ Export Excel</button>
  <button onClick={printValueSales} style={styles.exportBtn}>🖨️ Print</button>
</div>          </div>
          {topRevenue.filter(p => p.totalRevenue > 0).length === 0 ? (
            <div style={styles.emptyState}>
              <p style={styles.emptyTitle}>No revenue data</p>
              <p style={styles.emptySubtitle}>Add selling prices to products to track revenue</p>
            </div>
          ) : (
            <>
              <div style={styles.chartWrapper}>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={topRevenue.filter(p => p.totalRevenue > 0).slice(0, 12)} margin={{ top: 10, right: 20, left: 10, bottom: 60 }}>
                    <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} angle={-35} textAnchor="end" interval={0} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={v => `₹${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#ffffff', borderTop: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', borderLeft: '1px solid #e2e8f0', borderRadius: '8px' }}
                      formatter={(v) => [`₹${Math.round(v).toLocaleString('en-IN')}`, 'Revenue']}
                    />
                    <Bar dataKey="totalRevenue" radius={[6, 6, 0, 0]} name="Revenue">
                      {topRevenue.slice(0, 12).map((_, i) => (
                        <Cell key={i} fill={i === 0 ? '#16a34a' : i === 1 ? '#22c55e' : i === 2 ? '#4ade80' : '#bbf7d0'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {isMobile ? <MobileRevenueCards /> : (
                <div style={styles.tableWrapper}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        {['#', 'Product', 'Group', `Revenue (${periodLabel})`, 'Units Sold', 'Selling Price', 'Margin %'].map(h => (
                          <th key={h} style={styles.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {topRevenue.filter(p => p.totalRevenue > 0).map((p, i) => (
                        <tr key={i} style={styles.tr}>
                          <td style={{ ...styles.td, color: '#94a3b8', fontWeight: '600' }}>{i + 1}</td>
                          <td style={styles.tdName}>{p.name}</td>
                          <td style={styles.td}><span style={styles.groupBadge}>{p.group}</span></td>
                          <td style={styles.td}><span style={styles.revenueValue}>₹{Math.round(p.totalRevenue).toLocaleString('en-IN')}</span></td>
                          <td style={{ ...styles.td, color: '#64748b' }}>{p.totalUnits}</td>
                          <td style={{ ...styles.td, color: '#64748b' }}>{p.sellingPrice > 0 ? `₹${p.sellingPrice}` : '—'}</td>
                          <td style={{ ...styles.td, color: p.avgMargin ? '#16a34a' : '#94a3b8', fontWeight: '600' }}>
                            {p.avgMargin ? `${p.avgMargin}%` : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === 'leastsold' && (
        <div>
          <div style={styles.sectionHeader}>
            <p style={styles.sectionSubtitle}>
              Products with fewest units sold — {periodLabel} (excludes zero-sales products, see Dead Stock)
            </p>
          </div>
          {leastSellers.length === 0 ? (
            <div style={styles.emptyState}>
              <p style={styles.emptyTitle}>No sales data</p>
              <p style={styles.emptySubtitle}>Record transactions to see least sold products</p>
            </div>
          ) : isMobile ? <MobileLeastSoldCards /> : (
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {['Product', 'Group', `Units Sold (${periodLabel})`, `Revenue (${periodLabel})`].map(h => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {leastSellers.map((p, i) => (
                    <tr key={i} style={styles.tr}>
                      <td style={styles.tdName}>{p.name}</td>
                      <td style={styles.td}><span style={styles.groupBadge}>{p.group}</span></td>
                      <td style={styles.td}><span style={styles.slowValue}>{p.totalUnits}</span></td>
                      <td style={styles.td}>
                        {p.totalRevenue > 0
                          ? <span style={styles.revenueValue}>₹{Math.round(p.totalRevenue).toLocaleString('en-IN')}</span>
                          : <span style={{ color: '#cbd5e1' }}>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'deadstock' && (
        <div>
          <div style={styles.sectionHeader}>
            <p style={styles.sectionSubtitle}>
              {days === 0
                ? 'Products with stock on hand but zero sales ever recorded'
                : `Products with stock on hand but zero sales in the last ${days} days`}
            </p>
          </div>
          {deadStock.length === 0 ? (
            <div style={styles.emptyState}>
              <p style={styles.emptyTitle}>No dead stock</p>
              <p style={styles.emptySubtitle}>Every product with stock has at least one sale in this period 🎉</p>
            </div>
          ) : isMobile ? <MobileDeadStockCards /> : (
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {['Product', 'Group', 'Stock on Hand', 'Cost Price', 'Capital Tied Up'].map(h => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {deadStock.map((p, i) => {
                    const tied = computeCapitalTiedUp(p);
                    return (
                      <tr key={i} style={styles.tr}>
                        <td style={styles.tdName}>{p.name}</td>
                        <td style={styles.td}><span style={styles.groupBadge}>{p.group}</span></td>
                        <td style={{ ...styles.td, fontWeight: '600', color: '#1e293b' }}>{p.stock}</td>
                        <td style={{ ...styles.td, color: '#64748b' }}>{p.costPrice > 0 ? `₹${p.costPrice}` : '—'}</td>
                        <td style={styles.td}>
                          {tied != null
                            ? <span style={styles.deadValue}>₹{tied.toLocaleString('en-IN')}</span>
                            : <span style={{ color: '#cbd5e1' }}>—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Analytics() {
  const { orgId } = useAuth();
  const [products, setProducts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [days, setDays] = useState(30);
  const [activePage, setActivePage] = useState('inventory');
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(
      query(collection(db, 'products'), where('orgId', '==', orgId)),
      snap => setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    let q;
    if (days === 0) {
      q = query(
        collection(db, 'activityLog'),
        where('orgId', '==', orgId),
        where('transactionType', 'in', ['sale', 'writeoff']),
        orderBy('timestamp', 'desc'),
        limit(2000)
      );
    } else {
      const since = Timestamp.fromDate(new Date(Date.now() - days * 24 * 60 * 60 * 1000));
      q = query(
        collection(db, 'activityLog'),
        where('orgId', '==', orgId),
        where('transactionType', 'in', ['sale', 'writeoff']),
        where('timestamp', '>=', since),
        orderBy('timestamp', 'desc')
      );
    }
    return onSnapshot(q, snap => setLogs(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [orgId, days]);

  const pages = [
    { key: 'inventory', label: '📦 Inventory & Stock' },
    { key: 'valuesales', label: '💰 Value & Sales' },
  ];

  return (
    <div>
      <div style={{ marginBottom: '4px' }}>
        <h1 style={styles.title}>Analytics</h1>
      </div>

      <div style={{ ...styles.pageTabs, ...(isMobile ? { overflowX: 'auto' } : {}) }}>
        {pages.map(p => (
          <button
            key={p.key}
            onClick={() => setActivePage(p.key)}
            style={activePage === p.key ? styles.pageTabActive : styles.pageTabInactive}
          >
            {p.label}
          </button>
        ))}
      </div>

      {activePage === 'inventory' && (
        <InventoryPage
          products={products}
          logs={logs}
          days={days}
          setDays={setDays}
          isMobile={isMobile}
        />
      )}
      {activePage === 'valuesales' && (
        <ValueSalesPage
          products={products}
          logs={logs}
          days={days}
          setDays={setDays}
          isMobile={isMobile}
        />
      )}
    </div>
  );
}